import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { Modal, Button, Form, Tab, Tabs, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { fetchReportDefinitionById, ReportDefinition, updateReportDefinition } from '../../../../redux/slices/reportsSlice';
import { getZones } from '../../../../redux/slices/lists/zonesSlice';
import { AppDispatch, RootState } from '../../../../redux/store';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import { FolderSelect } from 'components/forms/FolderSelect';
import FormatDateTime from 'components/common/FormatDateTime';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

interface EditReportDefinitionProps {
  show: boolean;
  onClose: () => void;
  selectedReportId: number | null;
  onSave: (updatedReport: ReportDefinition) => void;
}

const EditReportDefinition: React.FC<EditReportDefinitionProps> = ({ show, onClose, selectedReportId, onSave }) => {
  const dispatch = useAppDispatch();
  const selectedReport = useSelector((state: RootState) => state.reports.selectedReport);
  const zones = useAppSelector((state) => state.listOfZones?.zones || []);
  const [error, setError] = useState<string | null>(null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Local state for editable fields
  const [formData, setFormData] = useState<Partial<ReportDefinition>>({
    name: '',
    query: '',
  });
  const isDirty = useUnsavedChanges(show && isInitialized, formData);

  useEffect(() => {
    if (!show) {
      setFormData({
        name: '',
        query: '',
      });
      setError(null);
      setShowCloseConfirm(false);
      setIsInitialized(false);
      return;
    }

    setIsInitialized(false);
    dispatch(getZones());

    if (selectedReportId) {
      dispatch(fetchReportDefinitionById(selectedReportId))
        .then((response: any) => {
          if (response.payload) {
            setFormData({
              name: response.payload.name,
              query: response.payload.query,
              zone_id: response.payload.zone_id,
              folder_id: response.payload.folder_id ?? null,
            });
            setIsInitialized(true);
          } else {
            setIsInitialized(true);
          }
        })
        .catch((error) => {
          setError('Failed to fetch report definition');
          console.error('Error fetching report definition:', error);
          setIsInitialized(true);
        });
    } else {
      setIsInitialized(true);
    }
  }, [dispatch, selectedReportId, show]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (selectedReport && formData.name && formData.query) {
      try {
        const updatedReport = {
          ...selectedReport,
          ...formData,
        };
        await dispatch(updateReportDefinition(updatedReport));
        onSave(updatedReport);
        onClose();
      } catch (error) {
        setError('Failed to save report definition');
        console.error('Error updating report:', error);
      }
    }
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleRequestClose} backdrop="static" size="xl" fullscreen="xl-down">
        <Modal.Header closeButton>
          <Modal.Title>Edit Report Definition</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <p className="text-danger">{error}</p>}
          {show && !isInitialized ? (
            <p>Loading...</p>
          ) : (
            <Tabs defaultActiveKey="details" id="edit-report-tabs" className="mb-3" variant="underline">
              <Tab eventKey="details" title="Details">
                {selectedReport ? (
                  <Form>
                    <Form.Group controlId="name" className="mt-3">
                      <Form.Label>Name</Form.Label>
                      <Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter report name" />
                    </Form.Group>

                    <Form.Group controlId="zone_id" className="mt-3">
                      <Form.Label>Zone</Form.Label>
                      <Form.Select
                        value={formData.zone_id ?? ''}
                        onChange={(e) => {
                          const zone_id = e.target.value ? Number(e.target.value) : undefined;
                          // Reset the (zone-scoped) folder when zone changes.
                          setFormData((prev) => ({
                            ...prev,
                            zone_id,
                            folder_id: null,
                          }));
                        }}
                      >
                        <option value="">Select a Zone</option>
                        {zones.map((z: any) => (
                          <option key={z.zone_id} value={z.zone_id}>
                            {z.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group controlId="folder_id" className="mt-3">
                      <Form.Label>Folder</Form.Label>
                      <FolderSelect
                        value={formData.folder_id ?? null}
                        onChange={(folder_id) => setFormData((prev) => ({ ...prev, folder_id }))}
                        zoneId={formData.zone_id ?? null}
                      />
                    </Form.Group>

                    <Form.Group controlId="query" className="mt-3">
                      <Form.Label>Query</Form.Label>
                      <Form.Control as="textarea" rows={5} name="query" value={formData.query} onChange={handleInputChange} placeholder="Enter SQL query" />
                    </Form.Group>
                  </Form>
                ) : (
                  <p>Loading...</p>
                )}
              </Tab>
              <Tab eventKey="audit" title="Audit Trail">
                {selectedReport?.audit_trail ? (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Timestamp</th>
                        <th>Type</th>
                        <th>User</th>
                        <th>Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedReport.audit_trail.map((audit) => (
                        <tr key={audit.audit_trail_id}>
                          <td>
                            <FormatDateTime value={audit.created_at} />
                          </td>
                          <td>{audit.audit_details.action}</td>
                          <td>{audit.user_name}</td>
                          <td>
                            {audit.audit_details.differences ? (
                              <div>
                                {audit.audit_details.differences.map((diff: any, index: number) => (
                                  <div key={index} style={{ marginBottom: '10px' }}>
                                    <strong>Property changed: {diff.field}</strong>
                                    <div>
                                      <strong>Old Value:</strong>{' '}
                                      <pre
                                        style={{
                                          whiteSpace: 'pre-wrap',
                                          margin: 0,
                                        }}
                                      >
                                        {JSON.stringify(diff.old_value, null, 2)}
                                      </pre>
                                    </div>
                                    <div>
                                      <strong>New Value:</strong>{' '}
                                      <pre
                                        style={{
                                          whiteSpace: 'pre-wrap',
                                          margin: 0,
                                        }}
                                      >
                                        {JSON.stringify(diff.new_value, null, 2)}
                                      </pre>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              'No differences available'
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p>No audit trail available</p>
                )}
              </Tab>
            </Tabs>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={!isInitialized || !formData.name || !formData.query}>
            Save and Close
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditReportDefinition;
